import { useEffect, useRef } from "react";

import { useSelector } from "react-redux";

import { selectExpandedFilter } from "../store/selectors";

const getFilterCardSelector = (filterKey: string): string =>
    `[data-automation="filter-panel.item-container.${filterKey}"]`;

const findVisibleFilterCard = (filterKey: string): Element | null => {
    const elements = document.querySelectorAll(getFilterCardSelector(filterKey));

    for (const element of elements) {
        const { width, height } = element.getBoundingClientRect();

        if (width > 0 && height > 0) {
            return element;
        }
    }

    return elements[0] ?? null;
};

/**
 * Keeps a programmatically expanded filter card in view. Expansion alone does not scroll the panel, so a
 * card mid-list (e.g. Industry Plus) can open off-screen after a banner handoff. Deferred to rAF and backed
 * by a MutationObserver for the pending-placeholder → live-card swap on demo entry.
 */
export const useScrollToExpandedFilter = (isFiltersLoading = false): void => {
    const expandedFilter = useSelector(selectExpandedFilter);

    const observerRef = useRef<MutationObserver | null>(null);

    useEffect(() => {
        if (!expandedFilter || isFiltersLoading) {
            return undefined;
        }

        const filterKey = expandedFilter;

        const scrollToCard = (): boolean => {
            const card = findVisibleFilterCard(filterKey);

            if (!card) {
                return false;
            }

            card.scrollIntoView({ behavior: "instant", block: "nearest" });

            return true;
        };

        const frame = requestAnimationFrame(() => {
            if (scrollToCard()) {
                return;
            }

            observerRef.current = new MutationObserver(() => {
                if (scrollToCard()) {
                    observerRef.current?.disconnect();
                    observerRef.current = null;
                }
            });

            observerRef.current.observe(document.body, { childList: true, subtree: true });
        });

        return () => {
            cancelAnimationFrame(frame);
            observerRef.current?.disconnect();
            observerRef.current = null;
        };
    }, [expandedFilter, isFiltersLoading]);
};
